import { AssemblyTypeEnum } from 'src/constants/assemblies'

/**
 * Checks if two usage types are compatible for copying assemblies.
 * Walls can only be copied to other walls.
 * Roof and Ceiling can only be copied to the same type (roof to roof, ceiling to ceiling).
 */
export const areUsageTypesCompatible = (
  sourceType: AssemblyTypes,
  targetType: AssemblyTypes,
): boolean => {
  const isSourceWall =
    sourceType === AssemblyTypeEnum.Inner || sourceType === AssemblyTypeEnum.Outer
  const isTargetWall =
    targetType === AssemblyTypeEnum.Inner || targetType === AssemblyTypeEnum.Outer

  // Walls can be copied to other walls
  if (isSourceWall && isTargetWall) {
    return true
  }

  // Roof and Ceiling can only be copied to the same type
  if (sourceType === targetType) {
    return true
  }

  return false
}

/**
 * Returns the list of usage types that are compatible with the source usage type for duplication.
 * Walls can only be duplicated to other walls.
 * Roof and Ceiling can only be duplicated to the same type (roof to roof, ceiling to ceiling).
 */
export const getAllowedUsageTypesForDuplication = (sourceType: AssemblyTypes): AssemblyTypes[] => {
  const isSourceWall =
    sourceType === AssemblyTypeEnum.Inner || sourceType === AssemblyTypeEnum.Outer

  if (isSourceWall) {
    return [AssemblyTypeEnum.Inner, AssemblyTypeEnum.Outer]
  }

  // For roof and ceiling, only allow the same type
  if (sourceType === AssemblyTypeEnum.Roof) {
    return [AssemblyTypeEnum.Roof]
  }

  if (sourceType === AssemblyTypeEnum.Ceiling) {
    return [AssemblyTypeEnum.Ceiling]
  }

  // Fallback: return all types if source type is unknown
  return [
    AssemblyTypeEnum.Inner,
    AssemblyTypeEnum.Outer,
    AssemblyTypeEnum.Roof,
    AssemblyTypeEnum.Ceiling,
  ]
}

/**
 * Determines the appropriate assembly kind for a target usage type.
 */
const getKindForUsageType = (
  currentKind: AssemblyKind,
  targetType: AssemblyTypes,
): AssemblyKind => {
  const isTimberFrame =
    currentKind === 'timber-frame-assembly' ||
    currentKind === 'timber-frame-assembly-wall' ||
    currentKind === 'timber-frame-assembly-roof' ||
    currentKind === 'timber-frame-assembly-ceiling'

  const isCLT =
    currentKind === 'clt-assembly' ||
    currentKind === 'clt-assembly-roof' ||
    currentKind === 'clt-assembly-ceiling'

  if (isTimberFrame) {
    if (targetType === AssemblyTypeEnum.Inner || targetType === AssemblyTypeEnum.Outer) {
      return 'timber-frame-assembly-wall'
    } else if (targetType === AssemblyTypeEnum.Roof) {
      return 'timber-frame-assembly-roof'
    } else if (targetType === AssemblyTypeEnum.Ceiling) {
      return 'timber-frame-assembly-ceiling'
    }
  }

  if (isCLT) {
    if (targetType === AssemblyTypeEnum.Roof) {
      return 'clt-assembly-roof'
    } else if (targetType === AssemblyTypeEnum.Ceiling) {
      return 'clt-assembly-ceiling'
    }
  }

  return currentKind
}

/**
 * Creates default vibration check settings for ceiling assemblies.
 * Note: The form schema uses 'requirement' (singular) but the type uses 'requirements' (plural).
 * We include both to satisfy the type and match form expectations.
 */
const createDefaultVibrationSettings = (): VibrationCheckSettings =>
  ({
    requirement: 'NoDemands', // For form schema (singular)
    requirements: 'NoDemands', // For type (plural)
    screed_data: {
      screed: 'NoScreed',
      fill: 'NoFill',
    },
    screed_height: 0.01,
    screed_e_modul: 10000000000,
    setting_type: 'vibration',
    slab_weight_per_area: 0,
    slab_width: 0,
    step_size: 0,
  } as VibrationCheckSettings)

/**
 * Prepares an assembly for duplication to a target usage type.
 * - Validates compatibility between source and target types
 * - Updates the assembly kind to match target type
 * - Handles vibration settings (adds defaults for roof→ceiling, removes for ceiling→roof)
 */
export const prepareAssemblyForDuplication = (
  assembly: Assembly,
  targetUsageType: AssemblyTypes,
): Assembly => {
  if (!areUsageTypesCompatible(assembly.usage_type, targetUsageType)) {
    throw new Error(
      `Cannot copy assembly from ${assembly.usage_type} to ${targetUsageType}. ` +
        `Only compatible types can be copied (walls ↔ walls, roof ↔ ceiling).`,
    )
  }

  const newKind = getKindForUsageType(assembly.kind, targetUsageType)
  const isSourceRoof = assembly.usage_type === AssemblyTypeEnum.Roof
  const isSourceCeiling = assembly.usage_type === AssemblyTypeEnum.Ceiling
  const isTargetRoof = targetUsageType === AssemblyTypeEnum.Roof
  const isTargetCeiling = targetUsageType === AssemblyTypeEnum.Ceiling

  const duplicated: Assembly = {
    ...assembly,
    guid: '', // Clear GUID for new assembly
    usage_type: targetUsageType,
    kind: newKind,
  }

  // Handle vibration settings based on source and target types
  if (isSourceRoof && isTargetCeiling) {
    // Roof → Ceiling: Add default vibration settings
    duplicated.vibration_check_settings = createDefaultVibrationSettings()
  } else if (isSourceCeiling && isTargetRoof) {
    // Ceiling → Roof: Remove vibration settings
    duplicated.vibration_check_settings = null
  } else if (isTargetCeiling && !duplicated.vibration_check_settings) {
    // If copying to ceiling and no settings exist, add defaults
    duplicated.vibration_check_settings = createDefaultVibrationSettings()
  } else if (isTargetRoof) {
    // If copying to roof, ensure no vibration settings
    duplicated.vibration_check_settings = null
  }

  return duplicated
}
